import type { AssetClassName, ChimneyAddressIssue, PortfolioItemTypeName } from 'src/gql/graphql';
import { isNil } from 'es-toolkit';
import { ItemType } from 'src/constants/account-link';

import {
  PERFORMANCE_TIME_PERIOD_ALL_TIME,
  PERFORMANCE_TIME_PERIOD_PAST_MONTH,
  PERFORMANCE_TIME_PERIOD_YEAR_TO_DATE,
} from '@savvy/constants/performance-time-periods';

import type { ClientPortalTimeRange } from '../common/constants';

export type AccountGroupId =
  | 'cash'
  | 'credit'
  | 'investments'
  | 'real-estate'
  | 'vehicles'
  | 'company-equity'
  | 'other-assets'
  | 'loans';

export type AddAccountItemType =
  | ItemType.BANK_ACCOUNT
  | ItemType.INVESTMENT_ACCOUNT
  | ItemType.REAL_ESTATE
  | ItemType.COMPANY_EQUITY
  | ItemType.OTHER_ASSET
  | ItemType.OTHER_LIABILITY;

type AccountGroupDefinition = {
  readonly id: AccountGroupId;
  readonly label: string;
  readonly kind: 'asset' | 'liability';
  readonly connectLabel: string;
  readonly connectItemType: AddAccountItemType;
  readonly connectFeedOnly?: boolean;
};

export const ACCOUNT_GROUPS: readonly AccountGroupDefinition[] = [
  { id: 'cash', label: 'Cash', kind: 'asset', connectLabel: 'Connect bank', connectItemType: ItemType.BANK_ACCOUNT },
  {
    id: 'credit',
    label: 'Credit Cards',
    kind: 'liability',
    connectLabel: 'Connect card',
    connectItemType: ItemType.BANK_ACCOUNT,
    connectFeedOnly: true,
  },
  {
    id: 'investments',
    label: 'Investments',
    kind: 'asset',
    connectLabel: 'Connect account',
    connectItemType: ItemType.INVESTMENT_ACCOUNT,
  },
  {
    id: 'real-estate',
    label: 'Real Estate',
    kind: 'asset',
    connectLabel: 'Add property',
    connectItemType: ItemType.REAL_ESTATE,
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    kind: 'asset',
    connectLabel: 'Add vehicle',
    connectItemType: ItemType.OTHER_ASSET,
  },
  {
    id: 'company-equity',
    label: 'Company Equity',
    kind: 'asset',
    connectLabel: 'Add company equity',
    connectItemType: ItemType.COMPANY_EQUITY,
  },
  {
    id: 'other-assets',
    label: 'Other Assets',
    kind: 'asset',
    connectLabel: 'Add asset',
    connectItemType: ItemType.OTHER_ASSET,
  },
  {
    id: 'loans',
    label: 'Loans',
    kind: 'liability',
    connectLabel: 'Add loan',
    connectItemType: ItemType.OTHER_LIABILITY,
  },
];

export const LIABILITY_GROUP_IDS: readonly AccountGroupId[] = ACCOUNT_GROUPS.filter(
  (group) => group.kind === 'liability',
).map((group) => group.id);

export const ACCOUNT_TYPE_TO_GROUP_ID: Record<PortfolioItemTypeName, AccountGroupId> = {
  depository: 'cash',
  credit: 'credit',
  investment: 'investments',
  fund_investment: 'investments',
  private_investment: 'investments',
  cryptocurrency: 'investments',
  employer_equity: 'company-equity',
  auto: 'loans',
  commercial: 'loans',
  consumer: 'loans',
  home_equity: 'loans',
  line_of_credit: 'loans',
  loan: 'loans',
  mortgage: 'loans',
  student: 'loans',
  general: 'other-assets',
  insurance: 'other-assets',
  other: 'other-assets',
};

export const ASSET_CLASS_TO_GROUP_ID: Record<AssetClassName, AccountGroupId> = {
  real_estate: 'real-estate',
  vehicle: 'vehicles',
  company_equity: 'company-equity',
  collectible: 'other-assets',
  insurance_policy: 'other-assets',
  other: 'other-assets',
};

export const ACCOUNTS_TITLE = 'Net worth';
export const ACCOUNTS_CONNECT_LABEL = 'Add assets';
export const ACCOUNTS_EXPORT_LABEL = 'Export';
export const NET_WORTH_CSV_FILE_NAME = 'net-worth.csv';
export const NET_WORTH_CSV_HEADER = [
  'Group',
  'Name',
  'Type',
  'Institution',
  'Account',
  'In net worth',
  'Balance',
  'Last updated',
] as const;
export const ACCOUNT_MASK_PREFIX = '···';

export function accountsManagedSubtitle(brandName: string): string {
  return `Managed by ${brandName}`;
}
export const ACCOUNTS_HELD_AWAY_MANAGEMENT_SUBTITLE = 'Managed by household';
export const ACCOUNTS_UNKNOWN_PROVIDER_SUBTITLE = 'Held away';

type AddAccountOption = {
  readonly itemType: AddAccountItemType;
  readonly label: string;
  readonly description: string;
};

export const ADD_ACCOUNT_OPTIONS: readonly AddAccountOption[] = [
  {
    itemType: ItemType.BANK_ACCOUNT,
    label: 'Bank account',
    description: 'Checking, savings or money market',
  },
  {
    itemType: ItemType.INVESTMENT_ACCOUNT,
    label: 'Investment account',
    description: 'Brokerage, retirement or 529',
  },
  {
    itemType: ItemType.REAL_ESTATE,
    label: 'Real estate',
    description: 'A home or investment property you own',
  },
  {
    itemType: ItemType.COMPANY_EQUITY,
    label: 'Company equity',
    description: 'Options, RSUs or private shares',
  },
  {
    itemType: ItemType.OTHER_ASSET,
    label: 'Other asset',
    description: 'A vehicle, collectible or policy',
  },
  {
    itemType: ItemType.OTHER_LIABILITY,
    label: 'Liability',
    description: 'A loan or mortgage',
  },
];

export const ADD_ACCOUNT_DIALOG_TITLE = 'Connect an account';
export const ADD_ACCOUNT_PICKER_HEADING = 'What would you like to add?';
export const ADD_ACCOUNT_LINKED_TOAST = 'Account connected';

export const ACCOUNTS_ERROR_TITLE = "Couldn't load your accounts";
export const ACCOUNTS_ERROR_DESCRIPTION = 'Your accounts and balances are safe. Try again, or reach out to your team.';
export const ACCOUNTS_RETRY_LABEL = 'Try again';

export const ACCOUNTS_EMPTY_TITLE = 'No accounts connected yet';
export const ACCOUNTS_EMPTY_DESCRIPTION =
  'Link the accounts you hold outside Savvy to see your full net worth in one place.';

export const ACCOUNTS_SKELETON_GROUPS = 2;

export const ACCOUNTS_ROW_MENU_LABEL = 'Account options';
export const ACCOUNTS_REFRESH_LABEL = 'Refresh now';
export const ACCOUNTS_REORDER_HANDLE_LABEL = 'Drag to reorder';
export const ACCOUNTS_MOVE_UP_LABEL = 'Move up';
export const ACCOUNTS_MOVE_DOWN_LABEL = 'Move down';
export const ACCOUNTS_REMOVE_LABEL = 'Remove account';
export const ACCOUNTS_REMOVE_CONFIRM_LABEL = 'Remove account';
export const ACCOUNTS_REMOVED_TOAST = 'Account removed';
export const ACCOUNTS_REMOVE_ERROR_TOAST = "Couldn't remove that account. Try again in a moment.";
export const ACCOUNTS_REFRESHED_TOAST = 'Balances refreshed';

export const ACCOUNTS_RENAME_LABEL = 'Rename account';
export const ACCOUNTS_RENAME_TITLE = 'Rename account';
export const ACCOUNTS_RENAME_DESCRIPTION =
  'Give {name} a custom name so you can tell it apart, like "House savings" or "For business use". Your advisor sees the same name.';
export const ACCOUNTS_RENAME_FIELD_LABEL = 'Custom name';
export const ACCOUNTS_RENAME_MAX_LENGTH = 60;
export const ACCOUNTS_RENAME_SUBMIT_LABEL = 'Save custom name';
export const ACCOUNTS_RENAME_CLEAR_LABEL = 'Delete custom name';
export const ACCOUNTS_RENAMED_TOAST = 'Custom name saved';
export const ACCOUNTS_RENAME_CLEARED_TOAST = 'Custom name deleted';
export const ACCOUNTS_RENAME_ERROR_TOAST = "Couldn't save that custom name. Try again in a moment.";

export const ACCOUNTS_UPDATE_BALANCE_LABEL = 'Update balance';
export const ACCOUNTS_UPDATE_BALANCE_TITLE = 'Update balance';
export const ACCOUNTS_UPDATE_BALANCE_DESCRIPTION =
  "Enter what {name} is worth today. We can't read this account automatically, so it stays at the figure you enter until you change it. Your advisor sees the same number.";
export const ACCOUNTS_UPDATE_BALANCE_FIELD_LABEL = 'Balance';
export const ACCOUNTS_UPDATE_BALANCE_SUBMIT_LABEL = 'Save balance';
export const ACCOUNTS_BALANCE_UPDATED_TOAST = 'Balance updated';
export const ACCOUNTS_UPDATE_BALANCE_ERROR_TOAST = "Couldn't save that balance. Try again in a moment.";

export const ROW_EXCLUDED_FROM_NET_WORTH_LABEL = 'Not counted in net worth';

export const ACCOUNTS_HIDE_FROM_NET_WORTH_LABEL = 'Hide from net worth';
export const ACCOUNTS_INCLUDE_IN_NET_WORTH_LABEL = 'Include in net worth';
export const ACCOUNTS_HIDDEN_FROM_NET_WORTH_TOAST = 'Removed from net worth';
export const ACCOUNTS_INCLUDED_IN_NET_WORTH_TOAST = 'Added back to net worth';
export const ACCOUNTS_NET_WORTH_ERROR_TOAST = "Couldn't update net worth. Try again in a moment.";

export const DEFAULT_NET_WORTH_RANGE: ClientPortalTimeRange = PERFORMANCE_TIME_PERIOD_PAST_MONTH;

export const NET_WORTH_PERIOD_CAPTIONS: Readonly<Record<ClientPortalTimeRange, string>> = {
  past_month: '1M change',
  past_three_months: '3M change',
  year_to_date: 'YTD change',
  past_year: '1Y change',
  all_time: 'change since inception',
};

export const NET_WORTH_LABEL = 'Net worth';
export const NET_WORTH_RANGE_CONTROL_LABEL = 'Net worth chart range';
export const NET_WORTH_CHART_LABEL = 'Net worth over time';

export const NET_WORTH_CHART_GRADIENT_ID = 'clientPortalNetWorthGradient';

export const ACCOUNTS_BALANCES_UPDATED_PREFIX = 'Balances updated';

export const NET_WORTH_FOOTER_ROW_HEIGHT_CLASS = 'h-6';

export const NET_WORTH_FOOTER_TEST_ID = 'client-portal-net-worth-footer';

export const NET_WORTH_CHART_EMPTY_TITLE = 'No balance history yet';
export const NET_WORTH_CHART_EMPTY_DESCRIPTION =
  "Your net-worth chart will appear here once we've tracked a few days of balances.";

export const BALANCE_CHART_ERROR_TITLE = "We couldn't load your balance history";
export const BALANCE_CHART_ERROR_DESCRIPTION =
  'Your accounts and balances are safe — this is just the chart. Try again in a moment.';
export const BALANCE_CHART_ERROR_RETRY_LABEL = 'Try again';

export const BALANCE_CHART_SHORT_RANGE_DESCRIPTION = 'There isn’t enough history to chart this range yet.';

export const NET_WORTH_CHART_UNAVAILABLE_DELTA_LABEL = 'As of your last sync';
export const NET_WORTH_CHART_PENDING_DELTA_LABEL = 'Awaiting balance history';

export const NET_WORTH_TWO_BASES_TOOLTIP_LABEL = 'Why the headline and the chart can differ';

export const NET_WORTH_TWO_BASES_CAPTION_AWAITING_COMPLIANCE =
  'Net worth above is your accounts and assets as they stand today — what you own minus what you owe. The chart is built from the values we have recorded over time, so anything we have not recorded history for yet can make the two figures differ.';

export const ACCOUNT_DETAIL_RANGE: ClientPortalTimeRange = PERFORMANCE_TIME_PERIOD_YEAR_TO_DATE;

export const ACCOUNT_POSITIONS_RETURN_PERIOD: ClientPortalTimeRange = PERFORMANCE_TIME_PERIOD_ALL_TIME;

export const ACCOUNT_DETAIL_BALANCE_LABEL = 'Current balance';

export const ACCOUNT_RETURN_TOOLTIP_LABEL = 'How this return is measured';

export const ACCOUNT_RETURN_UNAVAILABLE_LABEL = 'Performance is not available for this account yet';
export const ACCOUNT_RETURN_RANGE_CONTROL_LABEL = 'Account return period';
export const ACCOUNT_RETURN_CAPTION = 'return';
export const ACCOUNT_RETURN_FAILED_LABEL = 'We could not load this account’s performance';
export const ACCOUNT_ROW_RETURN_TEST_ID = 'client-portal-account-row-return';

export const ACCOUNT_RETURN_TOOLTIP_NOTE =
  'Time-weighted return{through}. It measures how the investments themselves performed, so money you paid in or took out is not counted as a gain or a loss.';

export const ACCOUNT_RETURN_PERIOD_CAPTIONS: Readonly<Record<ClientPortalTimeRange, string>> = {
  past_month: '1M return',
  past_three_months: '3M return',
  year_to_date: 'YTD return',
  past_year: '1Y return',
  all_time: 'return since inception',
};

export const ACCOUNT_RETURN_PERIOD_OPTION_LABELS: Readonly<Record<ClientPortalTimeRange, string>> = {
  past_month: '1 month',
  past_three_months: '3 months',
  year_to_date: 'Year to date',
  past_year: '1 year',
  all_time: 'Since inception',
};

export function netWorthReturnTooltipNote(brandName: string): string {
  return `Time-weighted return{through} on your ${brandName}-managed accounts. It measures how those investments performed, so money you paid in or took out is not counted as a gain or a loss. Held-away accounts, real estate and other assets are not included.`;
}
export const ACCOUNT_DETAIL_CHART_LABEL = 'Account balance over time';
export const ACCOUNT_DETAIL_RANGE_CONTROL_LABEL = 'Account balance chart range';
export const ACCOUNT_DETAIL_CHART_GRADIENT_ID = 'clientPortalAccountBalanceGradient';

export const ACCOUNT_DETAIL_CHART_EMPTY_TITLE = 'No balance history yet';
export const ACCOUNT_DETAIL_CHART_EMPTY_DESCRIPTION =
  "This account's balance chart will appear here once we've tracked a few days of balances.";

export const ACCOUNT_DETAIL_ERROR_TITLE = "Couldn't load this account";
export const ACCOUNT_DETAIL_ERROR_DESCRIPTION =
  'Your accounts and balances are safe. Try again, or head back to your accounts.';
export const ACCOUNT_DETAIL_BACK_LABEL = 'Back to accounts';

export const ACCOUNT_DETAIL_DETAILS_TITLE = 'Details';
export const ACCOUNT_DETAIL_TYPE_LABEL = 'Account type';
export const ACCOUNT_DETAIL_MANAGEMENT_LABEL = 'Management';
export const ACCOUNT_DETAIL_CUSTODIAN_LABEL = 'Custodian';
export const ACCOUNT_DETAIL_LAST_UPDATED_LABEL = 'Last updated';

export const ACCOUNT_DETAIL_MANAGED_BY_SAVVY = 'Managed by Savvy';
export const ACCOUNT_DETAIL_MANAGED_BY_HOUSEHOLD = 'Managed by household';
export const ACCOUNT_DETAIL_SELF_MANAGED = 'Self-managed';
export const ACCOUNT_DETAIL_UNKNOWN_TYPE = 'Account';
export const ACCOUNT_DETAIL_CHANGE_THROUGH_PREFIX = 'through';
export const ACCOUNT_DETAIL_SYNCED_PREFIX = 'synced';

export const ACCOUNT_DETAIL_SYNC_LABEL = 'Sync now';
export const ACCOUNT_DETAIL_SYNC_ERROR_TOAST =
  "Couldn't reach your institution to refresh this account. Try again in a moment.";

export const ACCOUNT_DETAIL_SYNC_POLL_INTERVAL_MS = 6_000;
export const ACCOUNT_DETAIL_SYNC_POLL_ATTEMPTS = 10;
export const ACCOUNT_DETAIL_SYNC_COOLDOWN_MS = 60_000;

export const ACCOUNT_DETAIL_SYNC_PENDING_TITLE = 'Refreshing your balances';
export const ACCOUNT_DETAIL_SYNC_PENDING_DESCRIPTION =
  "We've asked for the latest values for this account. They'll update here very soon — you don't need to wait on this page.";

export const ROW_UPDATED_PREFIX = 'Updated';

export const TRANSFER_VISIBLE_EMPTY_GROUP_IDS: readonly AccountGroupId[] = [
  'cash',
  'credit',
  'real-estate',
  'vehicles',
  'loans',
];

export const ACCOUNTS_GROUP_EMPTY_LABEL = 'No accounts connected yet.';

export const ACCOUNTS_CONNECT_NUDGE_LABEL = 'Connect account';

export const ACCOUNTS_CONNECT_NUDGE_DESCRIPTION =
  'We need a bit more information to give you an accurate summary. Link the accounts you hold outside Savvy to see your full net worth.';

export const ACCOUNTS_SUMMARY_TITLE = 'Summary';
export const ACCOUNTS_SUMMARY_ASSETS_LABEL = 'Assets';
export const ACCOUNTS_SUMMARY_LIABILITIES_LABEL = 'Liabilities';
export const SUMMARY_LIABILITY_SWATCH_COLOR = 'var(--destructive)';
export const ACCOUNTS_SUMMARY_ASSETS_TEST_ID = 'client-portal-accounts-summary-assets';
export const ACCOUNTS_SUMMARY_LIABILITIES_TEST_ID = 'client-portal-accounts-summary-liabilities';
export const ACCOUNTS_SUMMARY_LOADING_TEST_ID = 'client-portal-accounts-summary-loading';

export const ACCOUNT_DETAIL_TAB_QUERY_KEY = 'tab';
export const ACCOUNT_DETAIL_TAB_OVERVIEW = 'overview';
export const ACCOUNT_DETAIL_TAB_POSITIONS = 'positions';
export const ACCOUNT_DETAIL_TAB_TRANSACTIONS = 'transactions';

export const ACCOUNT_DETAIL_TABS = [
  { value: ACCOUNT_DETAIL_TAB_OVERVIEW, name: 'Overview' },
  { value: ACCOUNT_DETAIL_TAB_POSITIONS, name: 'Positions' },
  { value: ACCOUNT_DETAIL_TAB_TRANSACTIONS, name: 'Transactions' },
];

export const ACCOUNT_BANK_CREDIT_DETAIL_TABS = [
  { value: ACCOUNT_DETAIL_TAB_OVERVIEW, name: 'Overview' },
  { value: ACCOUNT_DETAIL_TAB_TRANSACTIONS, name: 'Transactions' },
];

export const ACCOUNT_DETAIL_TABS_CONTAINER_CLASS = 'overflow-x-auto px-4 md:px-10';

export const ACCOUNT_ALLOCATION_TITLE = 'Allocation';
export const ACCOUNT_ALLOCATION_ERROR_TITLE = 'We couldn’t load this account’s allocation';
export const ACCOUNT_ALLOCATION_EMPTY_MESSAGE = 'No holdings to break down yet.';
export const ACCOUNT_ALLOCATION_LEGEND_LABEL = 'Asset classes in this account';
export const ACCOUNT_ALLOCATION_LEGEND_CAP = 5;
export const ACCOUNT_ALLOCATION_SEE_ALL_LABEL = 'See all';
export const ACCOUNT_ALLOCATION_SHOW_LESS_LABEL = 'Show less';

export const ACCOUNT_BENEFICIARIES_TITLE = 'Beneficiaries';
export const ACCOUNT_BENEFICIARIES_ERROR_TITLE = 'We couldn’t load this account’s beneficiaries';
export const ACCOUNT_BENEFICIARIES_EMPTY_MESSAGE = 'No beneficiaries on file for this account.';
export const ACCOUNT_BENEFICIARIES_LIST_LABEL = 'Beneficiaries on this account';
export const ACCOUNT_BENEFICIARIES_PRIMARY_TIER = 'Primary';
export const ACCOUNT_BENEFICIARIES_CONTINGENT_TIER = 'Contingent';
export const ACCOUNT_BENEFICIARIES_ADVISOR_FALLBACK = 'your advisory team';
export const ACCOUNT_BENEFICIARIES_UNNAMED = 'Unnamed beneficiary';

export const ACCOUNT_SLEEVES_TITLE = 'Strategies';
export const ACCOUNT_SLEEVES_LIST_LABEL = 'Strategies in this account';
export const ACCOUNT_SLEEVE_DIRECT_INDEXING_STRATEGY = 'Direct Indexing';

export function accountSleevesCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'strategy' : 'strategies'}`;
}

export function accountSleevesToggleLabel(accountName: string): string {
  return `${accountName} strategies`;
}

export const ACCOUNT_INCOME_LABELS: Readonly<Record<ClientPortalTimeRange, string>> = {
  past_month: 'Dividends and interest (1M)',
  past_three_months: 'Dividends and interest (3M)',
  year_to_date: 'Dividends and interest (YTD)',
  past_year: 'Dividends and interest (1Y)',
  all_time: 'Dividends and interest (all time)',
};
export const ACCOUNT_INCOME_FOOTNOTE =
  'Dividends and interest include reinvested income. They exclude interest you paid, accruals and projections.';

export const ACCOUNT_POSITIONS_EMPTY_MESSAGE = 'No positions in this account yet.';
export const ACCOUNT_POSITIONS_CSV_SUFFIX = '-positions.csv';
export const ACCOUNT_TRANSACTIONS_CSV_SUFFIX = '-transactions.csv';

export const ACCOUNT_TRANSACTIONS_EMPTY_MESSAGE =
  'No transactions in this account yet. They appear here once activity is reported.';

export const STALE_UNKNOWN_INSTITUTION_LABEL = 'One of your institutions';

export const STALENESS_RECONNECT_LABEL = 'Reconnect';
export const STALENESS_DISMISS_STORAGE_KEY = 'client-portal-accounts-staleness-dismissed';

export function stalenessDisconnectedTitle(institutions: readonly string[], requiresSignIn: boolean) {
  if (institutions.length > 1) {
    return `${institutions.length} connections need attention`;
  }
  return requiresSignIn
    ? `${institutions[0]} needs you to sign in again`
    : `We've lost the connection to ${institutions[0]}`;
}

export function stalenessDisconnectedDescription(
  institutionList: string,
  lastSynced: string | null,
  requiresSignIn: boolean,
) {
  const cause = requiresSignIn
    ? `We can't refresh ${institutionList} until you sign in again.`
    : `We can't refresh ${institutionList} right now.`;
  const trailer = isNil(lastSynced)
    ? 'Reconnect to start seeing balances here.'
    : `The balances below are from ${lastSynced}.`;
  return `${cause} ${trailer}`;
}

export const STALENESS_RECONNECT_UNSUPPORTED_NOTE =
  'Your team is already on this one — reach out to them if the balances stay behind.';

export function stalenessReconnectLabel(institution: string) {
  return `Reconnect ${institution}`;
}

export function stalenessDelayedTitle(institutions: readonly string[]) {
  return institutions.length === 1
    ? `${institutions[0]} balances are taking longer than usual`
    : 'Some balances are taking longer than usual';
}

export function stalenessDelayedDescription(institutionList: string, lastSynced: string | null) {
  const opener = isNil(lastSynced)
    ? `Balances from ${institutionList} haven't refreshed recently.`
    : `Balances from ${institutionList} are from ${lastSynced}.`;
  return `${opener} We're working to refresh them — nothing has changed with your money in the meantime.`;
}

export const PROPERTY_ADDRESS_UPDATE_LABEL = 'Update address';

export function propertyAddressTitle(issue: ChimneyAddressIssue, street: string | null, name: string) {
  if (issue === 'missing' || isNil(street)) {
    return `Add the full address for ${name}`;
  }
  if (issue === 'lookup_failed') {
    return `We couldn't look up ${street} in public property records`;
  }
  return `We couldn't find ${street} in public property records`;
}

export function propertyAddressDescription(issue: ChimneyAddressIssue) {
  return issue === 'missing'
    ? "Add this property's address to keep its value up to date."
    : 'Update the address so we can keep its value up to date.';
}

export const UPDATE_PROPERTY_ADDRESS_TITLE = 'Update address';
export const UPDATE_PROPERTY_ADDRESS_DESCRIPTION =
  "Pick the address of {name} from the list. We use it to look up the property's value in public records.";
export const UPDATE_PROPERTY_ADDRESS_FIELD_LABEL = 'Address';
export const UPDATE_PROPERTY_ADDRESS_PLACEHOLDER = 'Start typing the property address';
export const UPDATE_PROPERTY_ADDRESS_PICK_ERROR = 'Pick an address from the list';
export const UPDATE_PROPERTY_ADDRESS_INCOMPLETE_ERROR =
  'Pick an address that includes a street, city, state and ZIP code';
export const UPDATE_PROPERTY_ADDRESS_SUBMIT_LABEL = 'Save';
export const UPDATE_PROPERTY_ADDRESS_ERROR = "We couldn't save the address. Try again.";
export const UPDATE_PROPERTY_ADDRESS_SUCCESS_TOAST = "Address saved. We'll look up the property's value shortly.";

export const PORTFOLIO_ITEM_DETAIL_LIABILITY_VALUE_LABEL = 'Balance owed';
export const PORTFOLIO_ITEM_DETAIL_ASSET_CHART_LABEL = 'Item value over time';
export const PORTFOLIO_ITEM_DETAIL_LIABILITY_CHART_LABEL = 'Balance owed over time';
export const PORTFOLIO_ITEM_DETAIL_CHART_GRADIENT_ID = 'clientPortalPortfolioItemValueGradient';

export const PORTFOLIO_ITEM_DETAIL_CHART_EMPTY_TITLE = 'No value history yet';
export const PORTFOLIO_ITEM_DETAIL_CHART_EMPTY_DESCRIPTION =
  "This item's chart will appear here once we've tracked a few days of values.";

export const PORTFOLIO_ITEM_DETAIL_ERROR_TITLE = "Couldn't load this item";
export const PORTFOLIO_ITEM_DETAIL_ERROR_DESCRIPTION =
  'Your accounts and balances are safe. Try again, or head back to your accounts.';

export const PORTFOLIO_ITEM_DETAIL_ASSET_TYPE_LABEL = 'Asset type';
export const PORTFOLIO_ITEM_DETAIL_LIABILITY_TYPE_LABEL = 'Liability type';
export const PORTFOLIO_ITEM_DETAIL_PROVIDER_LABEL = 'Provider';
export const PORTFOLIO_ITEM_DETAIL_ORIGINAL_BALANCE_LABEL = 'Original balance';
export const PORTFOLIO_ITEM_DETAIL_INTEREST_RATE_LABEL = 'Interest rate';

export const UPDATE_ITEM_VALUE_LABEL = 'Update value';
export const UPDATE_ITEM_VALUE_TITLE = 'Update value';
export const UPDATE_ITEM_VALUE_DESCRIPTION =
  'Enter what {name} is worth today. Your advisor sees the same figure, and your net worth updates right away.';
export const UPDATE_ITEM_VALUE_FIELD_LABEL = 'Value';
export const UPDATE_ITEM_VALUE_SUBMIT_LABEL = 'Save value';
export const UPDATE_ITEM_VALUE_SUCCESS_TOAST = 'Value updated.';
export const UPDATE_ITEM_VALUE_ERROR = "We couldn't update this value. Try again in a moment.";

export const PORTFOLIO_ITEM_DETAIL_MORTGAGE_BALANCE_LABEL = 'Mortgage balance';
export const ADD_MORTGAGE_LABEL = 'Add mortgage';
export const UPDATE_MORTGAGE_LABEL = 'Update mortgage';
export const UPDATE_MORTGAGE_SUBMIT_LABEL = 'Save mortgage';
export const MORTGAGE_ADDED_TOAST = 'Mortgage added.';
export const MORTGAGE_UPDATED_TOAST = 'Mortgage updated.';
export const UPDATE_MORTGAGE_ERROR = "We couldn't update this mortgage. Try again in a moment.";
export const UPDATE_ITEM_VALUE_ESTIMATE_PREFIX = 'Public property records estimate:';
export const UPDATE_ITEM_VALUE_USE_ESTIMATE_LABEL = 'Use this estimate';
export const PORTFOLIO_ITEM_VALUE_SOURCE_LABEL = 'Property value of {amount} from public property records';
export const PORTFOLIO_ITEM_VALUE_SOURCE_TOOLTIP_LABEL = 'About this value';
export const PORTFOLIO_ITEM_VALUE_SOURCE_TOOLTIP =
  'This value comes from public property records and refreshes weekly. You can change it anytime with Update value.';
